import { useState } from 'react';
import { FaSearch, FaUserShield } from 'react-icons/fa';
import PersonSearchModal from '../../modals/PersonSearchModal';
import { PersonSearchResult } from '../../types/api-responses';

// ============================================
// CONSTANTS & TYPES
// ============================================

export const MainRiskType = {
    DRAWEE: 'DRAWEE',
    ASSIGNOR: 'ASSIGNOR',
    OTHER: 'OTHER',
} as const satisfies Record<string, string>;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type MainRiskType = typeof MainRiskType[keyof typeof MainRiskType];

const TYPE_LABELS: Record<MainRiskType, string> = {
    DRAWEE: 'Sacado',
    ASSIGNOR: 'Cedente',
    OTHER: 'Outro',
};

export interface MainRisk {
    type: MainRiskType | '';
    uuid: string;
    name: string;
    document?: string;
}

interface MainRiskSelectorProps {
    value: MainRisk;
    onChange: (main_risk: MainRisk) => void;
    disabled?: boolean;
    errorMessage?: string;
}

interface MainRiskOptionProps {
    label: string;
    selected: boolean;
    disabled?: boolean;
    onSelect: () => void;
}

// ============================================
// HELPERS
// ============================================

const personToMainRisk = (person: PersonSearchResult): MainRisk => {
    const cnpjDoc = person.documents?.find((doc) => doc.type === 'CNPJ');
    const cpfDoc = person.documents?.find((doc) => doc.type === 'CPF');
    return {
        type: MainRiskType.OTHER,
        uuid: person.uuid,
        name: person.name,
        document: cnpjDoc?.number || cpfDoc?.number || '',
    };
};

// ============================================
// SUB-COMPONENTS
// ============================================

const MainRiskOption = ({ label, selected, disabled, onSelect }: MainRiskOptionProps) => (
    <label className={`flex items-center gap-2 rounded border px-4 py-2 transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${selected ? 'border-blue-400 bg-blue-50 dark:border-blue-500 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800'}`}>
        <input type="radio" name="receivables-main-risk" className="accent-blue-500"
            checked={selected} onChange={onSelect} disabled={disabled} />
        <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{label}</span>
    </label>
);

// ============================================
// MAIN COMPONENT
// ============================================

const MainRiskSelector = ({ value, onChange, disabled = false, errorMessage }: MainRiskSelectorProps) => {
    const [isPersonModalOpen, setIsPersonModalOpen] = useState(false);

    const selectType = (type: MainRiskType) => {
        if (type === MainRiskType.OTHER) {
            setIsPersonModalOpen(true);
            return;
        }
        // Sacado/Cedente: apenas o tipo, sem selecionar pessoa.
        onChange({ type, uuid: '', name: '', document: '' });
    };

    const handleSelectPerson = (person: PersonSearchResult) => {
        onChange(personToMainRisk(person));
    };

    const isOther = value.type === MainRiskType.OTHER;
    const selectedTypeLabel = value.type ? TYPE_LABELS[value.type] : '-';

    return (
        <div>
            <h3 className="text-md font-semibold mt-4 mb-3 text-gray-700 dark:text-gray-200">Risco Principal</h3>

            <div className="p-4 border rounded bg-gray-50 dark:bg-gray-700 dark:border-gray-600">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <MainRiskOption label="Sacado" selected={value.type === MainRiskType.DRAWEE} disabled={disabled}
                        onSelect={() => selectType(MainRiskType.DRAWEE)} />
                    <MainRiskOption label="Cedente" selected={value.type === MainRiskType.ASSIGNOR} disabled={disabled}
                        onSelect={() => selectType(MainRiskType.ASSIGNOR)} />
                    <MainRiskOption label="Outro" selected={isOther} disabled={disabled}
                        onSelect={() => selectType(MainRiskType.OTHER)} />
                    {isOther && (
                        <button type="button" disabled={disabled} onClick={() => setIsPersonModalOpen(true)}
                            className="inline-flex items-center gap-1 bg-gray-600 hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-400 text-white text-xs font-medium rounded px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed">
                            <FaSearch className="text-[10px]" /> {value.uuid ? 'Trocar registro' : 'Buscar registro'}
                        </button>
                    )}
                </div>

                {/* Apresentação do risco principal selecionado */}
                <div className="mt-4 flex items-start gap-3 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 shrink-0">
                        <FaUserShield />
                    </div>
                    <div className="min-w-0">
                        <p className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">{selectedTypeLabel}</p>
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                            {value.name || (value.type && value.type !== MainRiskType.OTHER ? `Definido pelo ${selectedTypeLabel.toLowerCase()} da operação` : 'Nenhum risco principal definido')}
                        </p>
                        {value.document && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">Documento: <span className="font-medium text-gray-700 dark:text-gray-300">{value.document}</span></p>
                        )}
                    </div>
                </div>

                {errorMessage && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{errorMessage}</p>}
            </div>

            <PersonSearchModal isOpen={isPersonModalOpen} onClose={() => setIsPersonModalOpen(false)} onSelect={handleSelectPerson} title="Buscar registro" />
        </div>
    );
};

export default MainRiskSelector;
